import React, { useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import {
  ArticleCard,
  ArticleImage,
  ArticleContent,
  ArticleTitle,
  ArticleSubtitle,
  Title,
  ArticleAdditionalText,
  ArticleTitleWrapper,
  HeaderContainer,
  ArticleScrollContainer,
} from "./more-articles.styles";
import CTAButton from "../../components/cta-button/cta-button";
import { ARTICLES } from "../../config/articleConfig";

interface MoreArticlesProps {
  className?: string;
  currentVisibleArticleId: number;
  onClickReadNextArticle: (articleId: number) => void;
}

const MoreArticles: React.FC<MoreArticlesProps> = ({
  className,
  currentVisibleArticleId,
  onClickReadNextArticle,
}) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Start Dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  // Stop Dragging
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handle Mouse Move
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Adjust scrolling speed
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  return (
    <div className={className}>
      <HeaderContainer>
        <div className="d-flex align-items-center" style={{ gap: 4, marginBottom: 16 }}>
          <Title>{t("components.more_articles.title")}</Title>
        </div>
      </HeaderContainer>

      <ArticleScrollContainer
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseUp}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
      >
        {ARTICLES.filter((article) => article.id !== currentVisibleArticleId).map((article) => (
          <ArticleCard key={article.id}>
            <ArticleImage src={article.image} alt={String(t(article.titleKey as any))} draggable={false} />
            <ArticleContent>
              <ArticleSubtitle>{t("articles.knowledge_hub_title")}</ArticleSubtitle>
              <ArticleTitleWrapper>
                <ArticleTitle>{t(article.titleKey as any)}</ArticleTitle>
                <ArticleAdditionalText>{t(article.descriptionKey as any)}</ArticleAdditionalText>
              </ArticleTitleWrapper>
              <div>
                <CTAButton
                  variant="ghost"
                  size="S"
                  label={t("components.more_articles.read_more")}
                  noPadding
                  onClick={() => onClickReadNextArticle(article.id)}
                />
              </div>
            </ArticleContent>
          </ArticleCard>
        ))}
      </ArticleScrollContainer>
    </div>
  );
};

export default MoreArticles;
